// Updated: 2026-08-13
import type { EnrichedPatternDisplay, PatternSearchResult } from '@mrb91002/shared-types'
import React, { useEffect, useState } from 'react'

import { useAuth } from '../../../context/AuthContext'
import {
  useNewestPatternsInfiniteQuery,
  usePatternsFilteredInfiniteQuery,
} from '../../../queries/browse/queries'

import { HomeBanner, PatternCards } from './components'

const SORT_OPTIONS = [
  { value: 'newest', label: 'Newest' },
  { value: 'most-reviewed', label: 'Most Reviewed' },
  { value: 'highest-rated', label: 'Highest Rated' },
] as const

type SortByValue = (typeof SORT_OPTIONS)[number]['value']

const DATE_RANGE_OPTIONS = [
  { value: 'all', label: 'All Time' },
  { value: 'week', label: 'This Week' },
  { value: 'month', label: 'This Month' },
  { value: 'year', label: 'This Year' },
] as const

type DateRangeValue = (typeof DATE_RANGE_OPTIONS)[number]['value']

/** Page size, unchanged from the pre-migration `loadPatterns`. */
const PAGE_LIMIT = 15

export const Home = () => {
  const { isLoading, session, idToken } = useAuth()
  const [sortBy, setSortBy] = useState<SortByValue>(SORT_OPTIONS[0].value)
  const [dateRange, setDateRange] = useState<DateRangeValue>(DATE_RANGE_OPTIONS[0].value)

  // Page load logging - runs once on mount
  useEffect(() => {
    console.log('📄 [PAGE] Home page loaded')
  }, [])

  // Home has TWO reads served by TWO different backends — DynamoDB (cursor) for "newest + all time",
  // OpenSearch (offset) for every other combination — so they are different RESOURCES, not one resource
  // with a sort applied. Exactly one is enabled at a time, selected by the same condition the
  // pre-migration `loadPatterns` branched on (react-query-browse-search, Req 3.8).
  //
  // The pagination state this replaces (`patterns`, `lastKey`, `from`, `hasMore`, `loading`,
  // `isLoadingMore`, `fetchError`, `hasSearched`) and the reset-on-filter-change effect are all gone: a
  // filter change now selects a different query KEY, so React Query resets accumulation for us and a
  // previously-visited combination paints from cache instead of refetching.
  const usingNewestBranch = sortBy === 'newest' && dateRange === 'all'
  const newestQuery = useNewestPatternsInfiniteQuery(PAGE_LIMIT, usingNewestBranch)
  const filteredQuery = usePatternsFilteredInfiniteQuery(sortBy, dateRange, PAGE_LIMIT, !usingNewestBranch)

  // Flattened in fetch order, so rendered ordering is identical to the old `[...prev, ...newItems]`.
  // The filtered branch keeps its `item !== undefined` guard — `SearchPatternsFilteredResponse.items` is
  // `(PatternSearchResult | undefined)[]`.
  const patterns: (EnrichedPatternDisplay | PatternSearchResult)[] = usingNewestBranch
    ? (newestQuery.data?.pages.flatMap((page) => page.items ?? []) ?? [])
    : (filteredQuery.data?.pages.flatMap((page) =>
        (page.items ?? []).filter((item): item is PatternSearchResult => item !== undefined),
      ) ?? [])

  const activeQuery = usingNewestBranch ? newestQuery : filteredQuery
  const loading = activeQuery.isPending
  const isLoadingMore = activeQuery.isFetchingNextPage
  const hasMore = activeQuery.hasNextPage
  const hasSearched = activeQuery.isSuccess
  const fetchError = activeQuery.error ? activeQuery.error.message : null

  // `fetchNextPage` is called through the branch rather than off `activeQuery` because the two hooks have
  // different `pageParam` types, so the union's call signature is not assignable.
  const loadMorePatterns = () => {
    void (usingNewestBranch ? newestQuery.fetchNextPage() : filteredQuery.fetchNextPage())
  }

  const handleSortChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const newSortBy = e.target.value as SortByValue
    setSortBy(newSortBy)
    // Reset date range to "all" when switching to "newest"
    if (newSortBy === 'newest') {
      setDateRange('all')
    }
  }

  const handleDateRangeChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setDateRange(e.target.value as DateRangeValue)
  }

  if (isLoading && patterns.length === 0 && !hasSearched) return <p>Loading...</p>

  console.log('[HOME] session', session)
  console.log('[HOME] idToken', idToken)
  console.log('[HOME] hostname:', window.location.hostname)

  return (
    <div
      style={{
        background: 'var(--cream-bg)',
        minHeight: '100vh',
        minWidth: 0,
        padding: 0,
        margin: 0,
      }}
    >
      <HomeBanner message />

      {/* <ExploreByCraft /> */}

      <main
        style={{
          padding: '80px 75px',
          position: 'relative',
        }}
      >
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            margin: '40px 0 32px 0',
          }}
        >
          <h2
            style={{
              fontWeight: 400,
              fontSize: '2.25rem',
              fontFamily: 'var(--font-heading)',
              margin: 0,
              letterSpacing: '-0.01em',
              color: 'var(--dark-brown)',
            }}
          >
            Newest Patterns
          </h2>
          <div style={{ display: 'flex', gap: 24, alignItems: 'center' }}>
            <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
              <label
                htmlFor="sort-select"
                style={{
                  fontSize: 16,
                  fontWeight: 500,
                  color: 'var(--medium-brown)',
                  fontFamily: 'var(--font-body)',
                }}
              >
                Sort:
              </label>
              <select
                id="sort-select"
                value={sortBy}
                onChange={handleSortChange}
                style={{
                  borderRadius: 9999,
                  padding: '8px 16px',
                  fontSize: 16,
                  border: '1px solid var(--border-gray)',
                  background: 'var(--white)',
                  fontWeight: 400,
                  fontFamily: 'var(--font-body)',
                  color: 'var(--medium-brown)',
                  cursor: 'pointer',
                }}
              >
                {SORT_OPTIONS.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>
            </div>
            <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
              <label
                htmlFor="date-range-select"
                style={{
                  fontSize: 16,
                  fontWeight: 500,
                  color: 'var(--medium-brown)',
                  fontFamily: 'var(--font-body)',
                }}
              >
                From:
              </label>
              <select
                id="date-range-select"
                value={dateRange}
                onChange={handleDateRangeChange}
                disabled={sortBy === 'newest'}
                style={{
                  borderRadius: 9999,
                  padding: '8px 16px',
                  fontSize: 16,
                  border: '1px solid var(--border-gray)',
                  background: sortBy === 'newest' ? 'var(--light-gray)' : 'var(--white)',
                  fontWeight: 400,
                  fontFamily: 'var(--font-body)',
                  color: sortBy === 'newest' ? 'var(--text-gray)' : 'var(--medium-brown)',
                  cursor: sortBy === 'newest' ? 'not-allowed' : 'pointer',
                }}
              >
                {DATE_RANGE_OPTIONS.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </div>

        <div style={{ marginBottom: 24 }}>
          {loading ? (
            <div style={{ textAlign: 'center', padding: '64px', color: '#64748b', fontSize: 16 }}>
              <div
                style={{
                  width: 32,
                  height: 32,
                  border: '3px solid #e2e8f0',
                  borderTop: '3px solid #3b82f6',
                  borderRadius: '50%',
                  animation: 'spin 1s linear infinite',
                  display: 'inline-block',
                  marginBottom: 16,
                }}
              />
              <div>Loading patterns...</div>
            </div>
          ) : patterns.length ? (
            <>
              <PatternCards patterns={patterns} />

              {/* Load More Button */}
              {hasMore && (
                <div style={{ textAlign: 'center', padding: '32px 0' }}>
                  <button
                    onClick={loadMorePatterns}
                    disabled={isLoadingMore}
                    style={{
                      padding: '12px 32px',
                      fontSize: 16,
                      fontWeight: 700,
                      fontFamily: 'var(--font-body)',
                      color: 'var(--white)',
                      background: isLoadingMore ? 'rgba(59, 130, 135, 0.7)' : 'var(--primary-teal)',
                      border: 'none',
                      borderRadius: 9999,
                      cursor: isLoadingMore ? 'not-allowed' : 'pointer',
                      transition: 'all 0.2s',
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: 12,
                      boxShadow: 'var(--shadow-button)',
                    }}
                    onMouseEnter={(e) => {
                      if (!isLoadingMore) {
                        e.currentTarget.style.transform = 'translateY(-2px)'
                        e.currentTarget.style.boxShadow = '0 12px 20px rgba(0, 0, 0, 0.15)'
                      }
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.transform = 'translateY(0)'
                      e.currentTarget.style.boxShadow = 'var(--shadow-button)'
                    }}
                  >
                    {isLoadingMore ? (
                      <>
                        <div
                          style={{
                            width: 16,
                            height: 16,
                            border: '2px solid rgba(255,255,255,0.3)',
                            borderTop: '2px solid var(--white)',
                            borderRadius: '50%',
                            animation: 'spin 1s linear infinite',
                          }}
                        />
                        Loading...
                      </>
                    ) : (
                      'Load More Patterns'
                    )}
                  </button>
                </div>
              )}
            </>
          ) : fetchError ? (
            <div
              style={{
                textAlign: 'center',
                padding: '64px',
                color: 'var(--danger)',
                fontSize: 16,
                background: '#fef2f2',
                borderRadius: 8,
                border: '1px solid #fecaca',
              }}
            >
              Error: {fetchError}
            </div>
          ) : hasSearched ? (
            <div
              style={{
                textAlign: 'center',
                padding: '64px',
                color: '#64748b',
                fontSize: 16,
                background: '#f8fafc',
                borderRadius: 8,
                border: '1px solid #e2e8f0',
              }}
            >
              <div style={{ fontSize: 18, fontWeight: 600, marginBottom: 8 }}>No results for this timeframe</div>
              <div>Try adjusting your filters or selecting a different time period</div>
            </div>
          ) : null}
        </div>
      </main>

      {/* <ShareYourSpark /> */}
    </div>
  )
}

export default Home
